import { logger } from '@heidi/runtime/logger';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { type PersonalizationDataResponse } from '../../contracts';
import { DELETE_PERSONALIZATION, GET_USER_PERSONALISATIONS } from '../../contracts/keys';
import { deletePersonalizationInsight } from '../api/user';

export const useMutateStateDeletePersonalizationInsight = () => {
  const deletePersonalizationInsightMutation = useMutationState({
    filters: { mutationKey: [DELETE_PERSONALIZATION] },
    select: (mutation) => mutation.state,
  });

  const currentMutation = deletePersonalizationInsightMutation.at(-1);
  const isPending = currentMutation?.status === 'pending';

  return { isPending };
};

export const useMutateDeletePersonalizationInsight = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [DELETE_PERSONALIZATION],
    mutationFn: deletePersonalizationInsight,
    onMutate: async ({ category, rule }) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER_PERSONALISATIONS] });

      queryClient.setQueryData<PersonalizationDataResponse>(
        [GET_USER_PERSONALISATIONS],
        (oldData) => {
          if (!oldData) return oldData;

          return produce(oldData, (draft) => {
            const categoryData = draft.personalization_data[category];
            if (!categoryData) return;

            const insightIndex = categoryData.findIndex(
              (insight) => insight.rule === rule
            );
            if (insightIndex === -1) return;

            categoryData.splice(insightIndex, 1);
          });
        }
      );
    },
    onError: (error) => {
      logger.error('Failed to delete personalization insight', error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [GET_USER_PERSONALISATIONS] });
    },
  });
};
